import React from 'react'
import PropTypes from 'prop-types'
import { useStaticQuery, graphql } from 'gatsby'

import Meta from './Meta'
import Substack from './Substack'

import Button from '../components/Button'
import Container from '../components/Container'
import Footer from '../components/Footer'
import Grid from '../components/Grid'
import GridCell from '../components/GridCell'
import Header from '../components/Header'
import Headline from '../components/Headline'
import Icon from '../components/Icon'
import IconLink from '../components/IconLink'
import NavLink from '../components/NavLink'
import PageLayout from '../components/PageLayout'
import Section from '../components/Section'

import twitterIcon from '../assets/icons/twitter.svg'
import slackIcon from '../assets/icons/slack.svg'
import githubIcon from '../assets/icons/github.svg'
import mailIcon from '../assets/icons/mail.svg'

import { defineCustomElements as deckDeckGoHighlightElement } from "@deckdeckgo/highlight-code/dist/loader";
deckDeckGoHighlightElement();

const contactIcons = {
  twitter: twitterIcon,
  slack: slackIcon,
  github: githubIcon,
  mail: mailIcon
}

const Page = ({
  title,
  description,
  children,
  location,
  hideNavigation = false,
  hideHeader = false
}) => {
  const { site } = useStaticQuery(
    graphql`
      query {
        site {
          siteMetadata {
            title
            appUrl
            substackFrameUrl
            termsUrl
            navLinks {
              name
              href
            }
            footerLinks {
              name
              href
            }
            contactLinks {
              key
              name
              href
            }
          }
        }
      }
    `
  )

  const headerChildren = site.siteMetadata.navLinks.map(({ href, name }) => (
    <NavLink key={href} href={href}>
      {name}
    </NavLink>
  ))

  return (
    <PageLayout
      header={
        !hideHeader && (
          <Header
            currentHref={location.href}
            siteTitle={site.siteMetadata.title}
            menuLabel="Menu"
            action={
              !hideNavigation &&
              site.siteMetadata.appUrl && (
                <Button pill invert href={site.siteMetadata.appUrl}>
                  Documentation
                </Button>
              )
            }
          >
            {!hideNavigation ? headerChildren : undefined}
          </Header>
        )
      }
      footer={
        <Footer currentHref={location.href} siteTitle={site.siteMetadata.title}>
          {site.siteMetadata.footerLinks.map(({ href, name }) => (
            <NavLink key={href} href={href}>
              {name}
            </NavLink>
          ))}
        </Footer>
      }
    >
      <Meta title={title} description={description} />

      {children}
      <Section>
        <Container>
          <Headline heroThin classNames={['u-space-xl']}>
            Keep in touch
          </Headline>
          <Grid>
            <GridCell m={6} l={4}>
              <Headline classNames={['u-space-base']}>
                Get the latest news
              </Headline>
              <Substack substackFrameUrl={site.siteMetadata.substackFrameUrl} />
            </GridCell>
            <GridCell m={6} l={4} lStart={9}>
              <Headline classNames={['u-space-base']}>Contact us</Headline>
              {site.siteMetadata.contactLinks.map(({ key, href, name }) => (
                <IconLink
                  key={key}
                  classNames={['u-space-s']}
                  icon={<Icon source={contactIcons[key]} />}
                  label={name}
                  href={href}
                  target="_blank"
                  block
                />
              ))}
            </GridCell>
          </Grid>
        </Container>
      </Section>
    </PageLayout>
  )
}

Page.propTypes = {
  description: PropTypes.string,
  title: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  location: PropTypes.object.isRequired,
  hideNavigation: PropTypes.bool,
  hideHeader: PropTypes.bool
}

export default Page
